import { useEffect, useState, type FormEvent } from "react";
import { TextField } from "@/components/TextField";
import { PhoneField } from "@/components/PhoneField";
import { normalizeUsPhone } from "@/lib/phone";
import { SubmitButton } from "@/components/SubmitButton";
import { useAuth } from "@/auth/AuthProvider";
import {
  getOwnProfile,
  updateOwnProfile,
  type BrokerProfile,
} from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// The broker's own profile (PMS-S063 / BRK-S019). Editable by every role
// (broker:edit, own scope) — name, phone, business unit. The sign-in email is
// shown read-only: changing it is a separate auth flow, so the API doesn't
// accept it here.

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

export function ProfileCard() {
  const { authedRequest } = useAuth();
  const [profile, setProfile] = useState<BrokerProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A number PMS would reject, caught before the round-trip (BRK-S073). The
  // profile phone stays a SCALAR — only accounts and personnel hold arrays.
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getOwnProfile(authedRequest);
        if (cancelled) return;
        setProfile(p);
        setFirstName(p.firstName);
        setLastName(p.lastName);
        setPhone(p.phone);
        setBusinessUnit(p.businessUnit ?? "");
      } catch (err) {
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUsPhone(phone);
    if (!normalized.ok) {
      setPhoneError(normalized.reason);
      setSaved(false);
      return;
    }
    setPhoneError(null);
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateOwnProfile(authedRequest, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        // Empty clears the optional field; the API rejects an empty string.
        businessUnit: businessUnit.trim() || null,
      });
      setProfile(updated);
      setSaved(true);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold">Your profile</h2>
        <p className="text-sm text-brk-dim">
          Your contact details. These are visible to Signal Specialty.
        </p>
      </div>

      {loadError && (
        <p role="alert" className="text-sm text-brk-danger">
          {loadError}
        </p>
      )}

      {!profile && !loadError && (
        <p role="status" className="text-sm text-brk-dim">
          Loading your profile…
        </p>
      )}

      {profile && (
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            <PhoneField
              label="Phone"
              value={phone}
              onChange={(v) => {
                setPhone(v);
                setPhoneError(null);
              }}
              error={phoneError ?? undefined}
              required
            />
            <TextField
              label="Business unit"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
            />
          </div>
          <TextField
            label="Sign-in email"
            value={profile.email}
            readOnly
            disabled
          />
          <p className="text-xs text-brk-dim">
            To change your sign-in email, contact Signal Specialty.
          </p>

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="text-sm font-medium text-green-700">
              ✓ Profile saved.
            </p>
          )}

          <SubmitButton pending={pending} pendingLabel="Saving…">
            Save profile
          </SubmitButton>
        </form>
      )}
    </section>
  );
}
